/**
 * Sync Prestige native localization form submits with Orbe cookies.
 * @see docs/orbe-localization-sync.md
 */

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 60;

const LOCALIZATION_FORM_SELECTORS = [
  '#header-localization-form',
  '#header-sidebar-localization-form',
  '#localization_form_footer',
];

/**
 * @param {string} hostname
 * @returns {string}
 */
export function stripLeadingWww(hostname) {
  return hostname.replace(/^www\./, '');
}

/**
 * Orbe shop cookie domain: orbito.getCountryDomain() or hostname without www.
 *
 * @param {{ getCountryDomain?: () => string | null | undefined } | undefined} orbito
 * @param {string} hostname
 * @returns {string}
 */
export function getOrbeCookieDomain(orbito, hostname) {
  if (orbito && typeof orbito.getCountryDomain === 'function') {
    const fromOrbe = orbito.getCountryDomain();
    if (fromOrbe) {
      return fromOrbe;
    }
  }
  return stripLeadingWww(hostname);
}

/**
 * @param {string | null | undefined} submitterName
 * @returns {'country' | 'locale' | null}
 */
export function localizationChangeKind(submitterName) {
  if (submitterName === 'country_code') {
    return 'country';
  }
  if (submitterName === 'locale_code') {
    return 'locale';
  }
  return null;
}

/**
 * Add or replace a query parameter on return_to.
 *
 * @param {string} returnTo
 * @param {string} name
 * @param {string} paramValue
 * @returns {string}
 */
function setParam(returnTo, name, paramValue) {
  if (returnTo.match(new RegExp('(\\?|&)' + name + '=[^&]*'))) {
    return returnTo.replace(new RegExp('(\\?|&)' + name + '=[^&]*'), '$1' + name + '=' + paramValue);
  }
  if (returnTo.includes('?')) {
    return `${returnTo}&${name}=${paramValue}`;
  }
  return `${returnTo}?${name}=${paramValue}`;
}

/**
 * Add or replace `country` and `oc` on return_to (Markets + Orbe cross-domain).
 *
 * @param {string} returnTo
 * @param {string} countryCode
 * @returns {string}
 */
export function setCountryOnReturnTo(returnTo, countryCode) {
  // country: replace or add. oc: replace or add (cross-domain)
  return setParam(setParam(returnTo, 'country', countryCode), 'oc', countryCode);
}

/**
 * Pure sync plan from a Prestige localization submit button.
 *
 * @param {{
 *   submitterName: string | null | undefined,
 *   submitterValue: string | null | undefined,
 *   returnTo: string,
 *   cookieDomain: string,
 * }} input
 * @returns {{ cookies: Array<{ name: string, value: string }>, returnTo: string } | null}
 */
export function planOrbeLocalizationSync(input) {
  const kind = localizationChangeKind(input.submitterName);
  if (!kind || !input.submitterValue) {
    return null;
  }

  if (kind === 'country') {
    return {
      cookies: [{ name: 'mdApp_countryCodeDomain', value: input.submitterValue }],
      returnTo: setCountryOnReturnTo(input.returnTo, input.submitterValue),
    };
  }

  return {
    cookies: [{ name: 'mdApp_showRecommendationLang', value: input.submitterValue }],
    returnTo: input.returnTo,
  };
}

/**
 * @param {string} name
 * @param {string} value
 * @param {string} domain
 * @returns {string}
 */
export function formatOrbeCookie(name, value, domain) {
  return `${name}=${value}; domain=${domain}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS};`;
}

/**
 * @param {Document} doc
 * @param {typeof getOrbeCookieDomain} getDomainFn
 */
function bindLocalizationForms(doc, getDomainFn) {
  for (const selector of LOCALIZATION_FORM_SELECTORS) {
    const form = doc.querySelector(selector);
    if (!form || form.dataset.orbeLocalizationSyncBound === 'true') {
      continue;
    }
    form.dataset.orbeLocalizationSyncBound = 'true';
    form.addEventListener(
      'submit',
      (event) => {
        const submitter = event.submitter;
        if (!submitter) {
          return;
        }

        const returnToInput = form.querySelector("input[name='return_to']");
        const returnTo = returnToInput instanceof HTMLInputElement ? returnToInput.value : '';

        const plan = planOrbeLocalizationSync({
          submitterName: submitter.name,
          submitterValue: submitter.value,
          returnTo,
          cookieDomain: '',
        });

        if (!plan) {
          return;
        }

        try {
          const domain = getDomainFn();
          for (const cookie of plan.cookies) {
            doc.cookie = formatOrbeCookie(cookie.name, cookie.value, domain);
          }
          if (returnToInput instanceof HTMLInputElement && plan.returnTo !== returnTo) {
            returnToInput.value = plan.returnTo;
          }
        } catch {
          /* Never block native POST /localization */
        }
      },
      { capture: true }
    );
  }
}

function initOrbeLocalizationSync() {
  bindLocalizationForms(document, () =>
    getOrbeCookieDomain(typeof globalThis.orbito !== 'undefined' ? globalThis.orbito : undefined, window.location.hostname)
  );
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOrbeLocalizationSync);
  } else {
    initOrbeLocalizationSync();
  }
}
